import './selfie-capture-wrapper/index.ts';
import SmartCamera from '../../../domain/camera/src/SmartCamera';
import styles from '../../../styles/src/styles';
import packageJson from '../../../../package.json';

const COMPONENTS_VERSION = packageJson.version;

const smartCameraWeb = document.querySelector('smart-camera-web');

// The wrapper→screens bridge is a set of `window`-level listeners
// (`selfie-capture.publish` / `.cancelled` / `.close`). Only ONE set should
// ever be active per realm: they all funnel into "the current capture
// session". We used to rely solely on each SelfieCaptureScreens instance
// removing its own listeners in `disconnectedCallback`, but `smart-camera-web`
// rebuilds its entire shadow tree on every attribute change (and the hosted
// product scripts set several attributes back-to-back on config), which can
// detach a SelfieCaptureScreens instance before its `disconnectedCallback`
// runs — leaking its `window` listeners. A leaked `publishHandler` re-fires
// the whole publish→submit chain, so a single capture would POST to
// `/v3/registration` (and every other product endpoint) twice. Tracking the
// active listener set at module scope lets each new registration evict any
// previous one unconditionally, independent of custom-element lifecycle
// timing.
let activeWrapperWindowListeners = null;

/**
 * Minimum-correct HTML attribute escape. `&` must be replaced first so the
 * subsequent `"` -> `&quot;` substitution isn't double-encoded. Used by every
 * getter that interpolates a partner-supplied value into the `innerHTML`
 * template in `connectedCallback` — without this, a value containing `"`
 * (or `&`) would break out of the attribute and inject markup.
 */
const escAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');

class SelfieCaptureScreens extends HTMLElement {
  constructor() {
    super();
    this.activeScreen = null;
    this._remountKey = 0; // Counter for forcing wrapper remounts
    smartCameraWeb?.dispatchEvent(new CustomEvent('metadata.initialize'));
  }

  connectedCallback() {
    this.innerHTML = `
            ${styles(this.themeColor)}
            <div style="height: 100%;">
              <selfie-capture-wrapper theme-color="${escAttr(this.themeColor)}" ${this.showNavigation} ${this.allowAgentMode} ${this.allowAgentModeTests} ${this.hideAttribution} ${this.disableImageTests} ${this.useStrictMode} ${this.showBackOnGuidelines} ${this.allowLegacySelfieFallback} ${this.hideInstructionsAttr} key="${this._remountKey}" hidden></selfie-capture-wrapper>
            </div>
        `;

    this._data = {
      images: [],
      meta: {
        libraryVersion: COMPONENTS_VERSION,
      },
    };
    // Enhanced SmartSelfie owns the whole selfie experience — guidelines,
    // capture and review — so this element hosts a single screen.
    this.selfieCapture = this.querySelector('selfie-capture-wrapper');

    this.setActiveScreen(this.selfieCapture);

    this.setUpEventListeners();
  }

  getAgentMode() {
    return this.inAgentMode ? 'environment' : 'user';
  }

  disconnectedCallback() {
    SmartCamera.stopMedia();
    smartCameraWeb?.dispatchEvent(new CustomEvent('metadata.cleanup'));

    if (this._selfieWrapperListeners) {
      this._selfieWrapperListeners.forEach(({ event, handler }) => {
        window.removeEventListener(event, handler);
      });
      if (activeWrapperWindowListeners === this._selfieWrapperListeners) {
        activeWrapperWindowListeners = null;
      }
      this._selfieWrapperListeners = null;
    }

    if (this.activeScreen) {
      this.activeScreen.removeAttribute('hidden');
    }
    this.activeScreen = null;
    this.innerHTML = '';
  }

  setUpEventListeners() {
    this.setupSelfieWrapperEventListeners();

    this.selfieCapture.addEventListener('selfie-capture-wrapper.close', () => {
      this.handleCloseEvent();
    });
  }

  // Force remount of selfie-capture-wrapper component for clean state
  async forceWrapperRemount() {
    SmartCamera.stopMedia();

    this._remountKey++;

    const container = this.querySelector('div');
    const oldWrapper = this.selfieCapture;

    if (oldWrapper && container) {
      // recreate wrapper element
      const newWrapper = document.createElement('selfie-capture-wrapper');

      // copy attributes from old wrapper, but override key
      const attributesToCopy = Array.from(oldWrapper.attributes);

      attributesToCopy.forEach((attr) => {
        newWrapper.setAttribute(attr.name, attr.value);
      });
      oldWrapper.remove();
      await new Promise((resolve) => {
        setTimeout(resolve, 50);
      });

      newWrapper.setAttribute('key', this._remountKey.toString());
      newWrapper.setAttribute('hidden', '');

      container.appendChild(newWrapper);

      this.selfieCapture = newWrapper;

      this.setUpEventListeners();
    }

    // give time for the new component to initialize
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 200);
    });
  }

  // Return to a clean selfie capture screen. Used when navigating back from the
  // document flow. Previously this was driven by toggling the `initial-screen`
  // attribute on this element, which re-fired a full `connectedCallback()`
  // rebuild every time (even when the value was unchanged). We now swap in a
  // fresh `selfie-capture-wrapper` synchronously and navigate explicitly, so we
  // land on a clean capture screen — not the stale review — without rebuilding
  // the whole screen tree or depending on timers.
  restartSelfieCapture() {
    SmartCamera.stopMedia();

    const container = this.querySelector('div');
    const oldWrapper = this.selfieCapture;

    if (oldWrapper && container) {
      this._remountKey++;

      const newWrapper = document.createElement('selfie-capture-wrapper');
      Array.from(oldWrapper.attributes).forEach((attr) => {
        newWrapper.setAttribute(attr.name, attr.value);
      });
      newWrapper.setAttribute('key', this._remountKey.toString());
      newWrapper.setAttribute('hidden', '');

      oldWrapper.remove();
      container.appendChild(newWrapper);

      this.selfieCapture = newWrapper;
      this.setUpEventListeners();
    }

    this.setActiveScreen(this.selfieCapture);
  }

  setActiveScreen(screen) {
    if (this.activeScreen === screen) {
      return;
    }

    this.activeScreen?.setAttribute('hidden', '');
    screen.removeAttribute('hidden');
    this.activeScreen = screen;
  }

  setupSelfieWrapperEventListeners() {
    // Evict any previously-registered window listeners — both this instance's
    // (normal remount) and any set left behind by a prior instance that was
    // detached before its `disconnectedCallback` ran (see
    // `activeWrapperWindowListeners`). Removing a non-registered listener is a
    // no-op, so this is safe even when the two sets overlap.
    if (this._selfieWrapperListeners) {
      this._selfieWrapperListeners.forEach(({ event, handler }) => {
        window.removeEventListener(event, handler);
      });
    }
    if (
      activeWrapperWindowListeners &&
      activeWrapperWindowListeners !== this._selfieWrapperListeners
    ) {
      activeWrapperWindowListeners.forEach(({ event, handler }) => {
        window.removeEventListener(event, handler);
      });
    }
    // Also remove the previously-attached element-level publish handler so we
    // don't accumulate duplicates across remounts (each call to
    // setupSelfieWrapperEventListeners would otherwise add another listener,
    // causing multiple transitions to review / multiple metadata events on
    // navigating back from document capture).
    if (this._selfieWrapperPublishHandler) {
      this.removeEventListener(
        'selfie-capture.publish',
        this._selfieWrapperPublishHandler,
      );
    }

    // Create new event handlers
    const cancelledHandler = async () => {
      SmartCamera.stopMedia();

      // Force remount of selfie-capture-wrapper for clean state on next visit
      await this.forceWrapperRemount();

      // ESS handles back navigation between its own views internally, so a
      // cancel that reaches here means the user backed out of the whole
      // selfie step — there is no earlier screen of ours to return to.
      this.handleBackEvents();
    };

    const closeHandler = async () => {
      SmartCamera.stopMedia();

      // Force remount of selfie-capture-wrapper for clean state on next visit
      await this.forceWrapperRemount();

      this.handleCloseEvent();
    };

    const publishHandler = (event) => {
      smartCameraWeb?.dispatchEvent(
        new CustomEvent('metadata.selfie-capture-end'),
      );
      this._data.images = event.detail.images;
      SmartCamera.stopMedia();

      // Enhanced SmartSelfie shows its own review screen and only dispatches
      // `publish` once the user confirms (marking it `reviewedInCapture`), so
      // there is no review step for us to insert — hand the images straight up
      // to the host.
      this._publishSelectedImages();
    };

    // Store references to remove them later
    this._selfieWrapperListeners = [
      { event: 'selfie-capture.cancelled', handler: cancelledHandler },
      { event: 'selfie-capture.close', handler: closeHandler },
      { event: 'selfie-capture.publish', handler: publishHandler },
    ];

    // Add event listeners
    this._selfieWrapperListeners.forEach(({ event, handler }) => {
      window.addEventListener(event, handler);
    });
    // Record this as the single active window-listener set for the realm, so
    // the next registration can evict it even if this instance is detached
    // without its `disconnectedCallback` ever running.
    activeWrapperWindowListeners = this._selfieWrapperListeners;

    // Also listen for the publish event on the parent SelfieCaptureScreens element
    // in case smartselfie-capture dispatches it there
    this._selfieWrapperPublishHandler = publishHandler;
    this.addEventListener('selfie-capture.publish', publishHandler);
  }

  _publishSelectedImages() {
    this.dispatchEvent(
      new CustomEvent('selfie-capture-screens.publish', { detail: this._data }),
    );
  }

  get hideInstructions() {
    return (
      this.hasAttribute('hide-instructions') ||
      this.getAttribute('initial-screen') === 'selfie-capture'
    );
  }

  /**
   * Forwarded to Enhanced SmartSelfie, which owns the guidelines screen this
   * attribute skips.
   */
  get hideInstructionsAttr() {
    return this.hideInstructions ? 'hide-instructions="true"' : '';
  }

  get hideAttribution() {
    return this.hasAttribute('hide-attribution') ? 'hide-attribution=""' : '';
  }

  get hideBackOfId() {
    return this.hasAttribute('hide-back-of-id');
  }

  get showNavigation() {
    return this.hasAttribute('show-navigation') ? 'show-navigation=""' : '';
  }

  get inAgentMode() {
    return this.getAttribute('allow-agent-mode') === 'true';
  }

  get allowAgentMode() {
    return this.inAgentMode ? 'allow-agent-mode="true"' : '';
  }

  get allowAgentModeTests() {
    return this.hasAttribute('show-agent-mode-for-tests')
      ? 'show-agent-mode-for-tests=""'
      : '';
  }

  get disableImageTests() {
    return this.hasAttribute('disable-image-tests')
      ? 'disable-image-tests=""'
      : '';
  }

  get useStrictMode() {
    return this.hasAttribute('use-strict-mode') &&
      this.getAttribute('use-strict-mode') !== 'false'
      ? 'use-strict-mode="true"'
      : '';
  }

  get showBackOnGuidelines() {
    return this.hasAttribute('show-back-on-guidelines')
      ? 'show-back-on-guidelines="true"'
      : '';
  }

  get allowLegacySelfieFallback() {
    return this.hasAttribute('allow-legacy-selfie-fallback')
      ? 'allow-legacy-selfie-fallback="true"'
      : '';
  }

  get themeColor() {
    return this.getAttribute('theme-color') || '#001096';
  }

  handleBackEvents() {
    this.dispatchEvent(new CustomEvent('selfie-capture-screens.cancelled'));
  }

  handleCloseEvent() {
    this.dispatchEvent(new CustomEvent('selfie-capture-screens.close'));
  }

  static get observedAttributes() {
    return [
      'title',
      'hidden',
      'show-navigation',
      'initial-screen',
      'allow-agent-mode',
      'show-agent-mode-for-tests',
      'disable-image-tests',
      'use-strict-mode',
      'show-back-on-guidelines',
      'allow-legacy-selfie-fallback',
    ];
  }

  attributeChangedCallback(name) {
    switch (name) {
      case 'title':
      case 'hidden':
      case 'initial-screen':
      case 'allow-agent-mode':
      case 'show-agent-mode-for-tests':
      case 'disable-image-tests':
      case 'use-strict-mode':
      case 'show-back-on-guidelines':
      case 'allow-legacy-selfie-fallback':
        this.connectedCallback();
        break;
      default:
        break;
    }
  }
}

if (
  'customElements' in window &&
  !customElements.get('selfie-capture-screens')
) {
  customElements.define('selfie-capture-screens', SelfieCaptureScreens);
}

export default SelfieCaptureScreens;
